import Storage from '/app/utils/storage';
import { bus } from '/app/plugins/bus';
import { OPEN_REQUESTED } from '/app/constant/game-dash';

/**
 * The moments that open the Game Dash on the user's behalf.
 *
 * Each one is a teaching moment rather than a notification: the user has just
 * earned something, and the dash is where that something landed. One key per
 * moment, so a user who has already been shown the dash there is never shown
 * it there again.
 */
export const NUDGE = {
    /** End of a rating flow, for players still on their first brain level. */
    RATING_END: 'rating-end',
    /** First topic or comment-reply the user posts. One key for both: the
     *  lesson is the same, and posting twice should not open the dash twice. */
    FIRST_POST: 'first-post',
    /** Landing on /my-dashboard. */
    DASHBOARD_VISIT: 'dashboard-visit',
    /** The share step of the challenge creation flow, first challenge only. */
    FIRST_CHALLENGE: 'first-challenge',
};

export const SCOPE = {
    /** Shown once and never again on this device. */
    ONCE: 'once',
    /** Shown once per browsing session - survives in-app navigation, not a
     *  new tab or a reload of the day. */
    SESSION: 'session',
};

const KEY = 'gamedash:auto-open';

/**
 * Session storage, with the same "never throw" contract as {@link Storage}.
 * Private-mode Safari and locked-down embeds make `sessionStorage` itself
 * throw on access, so the whole read goes through try/catch.
 */
const session = {
    get() {
        try {
            return JSON.parse(self.sessionStorage.getItem(KEY)) || {};
        } catch {
            return {};
        }
    },
    set(value) {
        try {
            self.sessionStorage.setItem(KEY, JSON.stringify(value));
        } catch {
            // A nudge we cannot remember is better than a nudge that throws.
        }
    },
};

const read = (scope) => (scope === SCOPE.SESSION ? session.get() : Storage.get(KEY, {}));

const write = (scope, value) => (scope === SCOPE.SESSION ? session.set(value) : Storage.set(KEY, value));

/**
 * Whether this nudge has already opened the dash within its scope.
 *
 * @param {string} nudge - One of {@link NUDGE}
 * @param {string} [scope] - One of {@link SCOPE}, default `once`
 * @returns {boolean}
 */
export const hasNudged = (nudge, scope = SCOPE.ONCE) => Boolean(read(scope)[nudge]);

/**
 * Records that this nudge has been spent.
 *
 * @param {string} nudge - One of {@link NUDGE}
 * @param {string} [scope] - One of {@link SCOPE}, default `once`
 * @returns {void}
 */
export const markNudged = (nudge, scope = SCOPE.ONCE) => {
    write(scope, { ...read(scope), [nudge]: true });
};

/**
 * How long a request stays worth replaying to a dialog that mounted late.
 * Long enough to cover an auth round-trip on a cold load, short enough that a
 * request cannot resurface in a later part of the visit.
 */
const PENDING_TTL_MS = 15000;

/** Last request emitted, kept for a dialog that was not listening yet. */
let pending = null;

/**
 * Asks the Game Dash to open itself.
 *
 * Fire-and-forget: the dialog lives in the navbar and decides whether the
 * request survives - it is the only place that knows the dash is mounted and
 * the user signed in, so it is also the only place that spends the nudge.
 * Calling this from a page where the dash cannot open costs nothing.
 *
 * The delay exists because every caller here sits right after an animation the
 * user is still reading: a points bubble in flight, a confetti burst, a page
 * settling after a route change. Opening on top of that reads as an
 * interruption; opening just after it reads as the destination. It is applied
 * here rather than in the dialog so the wait also covers the dialog not being
 * mounted yet - on a cold load it appears only once `me` has come back.
 *
 * @param {string} [nudge] - One of {@link NUDGE}. Omit for an unconditional
 *        open that is neither gated nor remembered.
 * @param {object} [options]
 * @param {string} [options.scope] - One of {@link SCOPE}, default `once`
 * @param {number} [options.delay] - Milliseconds to wait, default 600
 * @returns {void}
 */
export const nudgeGameDash = (nudge, { scope = SCOPE.ONCE, delay = 600 } = {}) => {
    setTimeout(() => {
        pending = { nudge, scope, at: Date.now() };

        bus(OPEN_REQUESTED, pending);
    }, delay);
};

/**
 * Hands the dialog a request that was emitted before it existed.
 *
 * Read once, at mount. Requests older than {@link PENDING_TTL_MS} are dropped
 * rather than replayed - by then the moment they belonged to has passed.
 *
 * @returns {{nudge: string, scope: string}|null}
 */
export const takePendingNudge = () => {
    const request = pending;

    pending = null;

    if (!request || Date.now() - request.at > PENDING_TTL_MS) {
        return null;
    }

    return request;
};

/** Drops the replay copy once a live listener has taken the request. */
export const clearPendingNudge = () => {
    pending = null;
};
